import { useState, useEffect } from 'react';
import LandingPage from '../components/LandingPage';
import { detectLocale } from '../locales/config';

const faqItems = [
  {
    q: "How does the debt calculator work for South Africa?",
    a: "Enter each debt — credit card, personal loan, store account, or vehicle finance — with the outstanding balance, annual interest rate, and minimum monthly payment. The debt calculator South Africa applies the snowball or avalanche method to show your exact debt-free date, total interest paid, and how much you save by paying extra each month.",
  },
  {
    q: "What is the debt snowball method?",
    a: "The debt snowball method means paying the minimum on all your debts and putting every extra rand toward your smallest debt first. Once cleared, that payment rolls into the next smallest. Each debt you eliminate frees up more cash to attack the next. The avalanche method targets the highest interest rate first — often your credit card — and saves more in total interest.",
  },
  {
    q: "Can I use this for store cards and personal loans?",
    a: "Yes. This debt repayment South Africa calculator works for all debt types — credit cards, store accounts (Woolworths, Edgars, Truworths), personal loans, vehicle finance, home loans, and payday loans. Enter multiple debts at once to get your complete debt consolidation plan and see exactly when you will be debt free in South Africa.",
  },
  {
    q: "How long will it take to become debt free in South Africa?",
    a: "It depends on your balance, interest rate, and monthly payment. A credit card balance of R20,000 at 22% per year paying only the minimum can take over 8 years to clear. Adding R500–R1,000 extra per month can cut that to under 3 years and save thousands in interest. Use this pay off debt South Africa calculator to see your personalised plan.",
  },
  {
    q: "What is debt counselling in South Africa?",
    a: "Debt counselling is a legal process under the National Credit Act where a registered debt counsellor negotiates reduced payments with creditors on your behalf. It protects you from legal action while you repay. Our free debt counselling calculator lets you first explore self-managed repayment — you may be able to pay off debt in South Africa without formal counselling by using the snowball or avalanche method.",
  },
];

const COLORS = { text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0" };

const sections = [
  {
    heading: 'How to use this debt repayment South Africa calculator',
    body: 'Enter each debt — credit card, store account, personal loan, or vehicle finance — with its outstanding balance, annual interest rate, and minimum monthly payment. Add any extra amount you can put toward repayment each month. The debt calculator South Africa shows your exact debt-free date, total interest cost, and how every extra rand speeds up getting out of debt.',
  },
  {
    heading: 'Debt snowball vs avalanche for South Africa',
    body: (
      <>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 12 }}>
          The <strong style={{ color: COLORS.text }}>debt snowball South Africa</strong> approach pays
          off your smallest debt first, then rolls that payment into the next. Clearing store accounts
          and smaller debts quickly builds momentum and shows real progress on how to get out of debt
          in South Africa.
        </p>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 0 }}>
          The <strong style={{ color: COLORS.text }}>avalanche method</strong> targets the highest interest
          rate first — usually your credit card at 20%+ per year. This debt consolidation calculator ZA
          approach saves the most money in total interest and gets you debt free South Africa faster
          on paper. Use both in the calculator to compare your exact saving.
        </p>
      </>
    ),
  },
  {
    heading: 'Why minimum payments keep South Africans in debt longer',
    body: 'Credit card interest in South Africa typically runs at 18–22% per year, and store cards often charge even more. Paying only the minimum on a R15,000 balance can trap you in debt for 7–10 years and cost nearly as much again in interest. Adding even R300–R500 extra per month can cut years off your timeline. Use this pay off debt South Africa calculator to see the exact impact before considering formal debt counselling.',
  },
];

export default function SouthAfricaDebtCalculator() {
  const [locale, setLocale] = useState('en-za');

  useEffect(() => {
    detectLocale().then(detected => {
      setLocale(detected === 'en-za' ? 'en-za' : 'en-za');
    });
  }, []);

  return (
    <LandingPage
      locale={locale}
      pageUrl="https://clearmydebt.app/za/"
      helmet={{
        lang: 'en',
        title: 'Free Debt Calculator South Africa — Pay Off Credit Card Debt | ClearMyDebt',
        description: "Calculate when you'll be debt free in South Africa. Snowball and avalanche method. No signup. Instant results. Works for credit cards, personal loans and store cards.",
        ogTitle: 'Free Debt Calculator South Africa — Pay Off Credit Card Debt | ClearMyDebt',
        ogDescription: "Calculate when you'll be debt free in South Africa. No signup. Instant results. Works for credit cards, personal loans and store cards.",
      }}
      hreflangList={[
        { hreflang: 'en-za', href: 'https://clearmydebt.app/za/' },
        { hreflang: 'x-default', href: 'https://clearmydebt.app/za/' },
      ]}
      h1="Free Debt Calculator South Africa"
      intro="Enter your debts and find out exactly when you will be debt free in South Africa — and how much you save with the right repayment strategy."
      sections={sections}
      faqItems={faqItems}
      faqHeading="Frequently Asked Questions"
      moreHeading="More Calculators"
      moreLinks={[
        { href: '/in/', label: 'Debt Calculator India' },
        { href: '/en-za/credit-card-payoff-calculator/', label: 'Credit Card Payoff' },
        { href: '/en-za/debt-snowball-calculator/', label: 'Debt Snowball' },
        { href: '/en-za/debt-avalanche-calculator/', label: 'Debt Avalanche' },
      ]}
    />
  );
}
